import {
  Column,
  DefaultCellTypes,
  NumberCell,
  Row,
  TextCell,
} from "@silevis/reactgrid";
import { useEffect, useMemo, useState } from "react";
import useDataverseService from "../../services/DataverseService";
import { SortableHeaderCellTemplate } from "../CellTemplates/SortableHeaderCell";
import { SummaryCellTemplate } from "../CellTemplates/SummaryCell";
import { useData } from "../Contexts/DataProvider";
import { useSelectedEntity } from "../Contexts/SelectedEntityProvider";
import { useSelectedTab } from "../Contexts/SelectedTabProvider";
import Loading from "../Loading/Loading";
import BaseTable from "./BaseTable";
import { numericFields, slovakMonthHeaders, sumFields } from "./TableHelpers";

const diacriticsRegex = /[\u0300-\u036f]/g;

export const normalize = (str: string) => {
  return (str ?? "")
    .normalize("NFD")
    .replace(diacriticsRegex, "")
    .toLowerCase()
    .trim();
};

const getNormalizedCategoryMap = (data: any[], categoryField: string) => {
  const map = new Map<string, string>();
  data.forEach((r) => {
    let raw = r[categoryField] ?? "Iné";
    let norm = normalize(raw);
    if (!map.has(norm)) map.set(norm, raw);
  });
  return map;
};

export const buildSummaryRowsByCategory = ({
  data,
  categories,
  monthFields,
  monthHeaders,
  categoryField,
  groupFn = (x: string) => x,
  normToLabel = (x: string) => x,
  summaryColumn,
  unifiedSummary,
}: {
  data: any[];
  categories: string[];
  monthFields: string[];
  monthHeaders: string[];
  categoryField: string;
  groupFn?: (categoryRaw: string) => string;
  normToLabel?: (norm: string) => string;
  summaryColumn: boolean;
  unifiedSummary: boolean;
}) => {
  const columns: Column[] = [
    { columnId: "category", width: 180, resizable: true },
    ...monthHeaders.map((m) => ({ columnId: m, width: 90, resizable: true })),
    ...(summaryColumn
      ? [{ columnId: "Summary", width: 110, resizable: true }]
      : []),
  ];

  const headerRow: Row<TextCell> = {
    rowId: "header",
    height: 20,
    cells: [
      {
        type: "text",
        text: "",
        groupId: "category",
        readOnly: true,
      } as TextCell,
      ...monthHeaders.map(
        (m) =>
          ({
            type: "text",
            text: m,
            groupId: m,
            readOnly: true,
          } as TextCell)
      ),
      ...(summaryColumn
        ? [
            {
              type: "text",
              text: unifiedSummary ? "Spolu" : "Summary",
              groupId: "Summary",
              readOnly: true,
            } as TextCell,
          ]
        : []),
    ],
  };

  const rows: Row<DefaultCellTypes>[] = categories.map((catLabel, idx) => {
    const catNorm = groupFn(catLabel);
    let rowTotal = 0;
    const cells: DefaultCellTypes[] = [
      {
        type: "text",
        text: normToLabel(catNorm),
        groupId: "category",
        readOnly: true,
      } as TextCell,
      ...monthFields.map((mf, i) => {
        const value = data
          .filter((item) => groupFn(item[categoryField] ?? "Iné") === catNorm)
          .reduce((sum, item) => sum + (Number(item[mf]) || 0), 0);
        rowTotal += value;
        return {
          type: "number",
          value,
          groupId: monthHeaders[i],
          readOnly: true,
        } as NumberCell;
      }),
      ...(summaryColumn
        ? [
            {
              type: "number",
              value: rowTotal,
              groupId: "Summary",
              readOnly: true,
            } as NumberCell,
          ]
        : []),
    ];
    return { rowId: idx, height: 20, cells };
  });

  const summaryRow: Row<DefaultCellTypes> = {
    rowId: "summary",
    height: 20,
    cells: [
      {
        type: "text",
        text: unifiedSummary ? "Spolu" : "Summary",
        groupId: "category",
        readOnly: true,
      } as TextCell,
      ...monthHeaders.map((header, idx) => {
        const total = rows.reduce((sum, row) => {
          const cell = row.cells[idx + 1];
          return (
            sum + (cell && "value" in cell ? (cell as NumberCell).value : 0)
          );
        }, 0);
        return {
          type: "number",
          value: total,
          groupId: header,
          readOnly: true,
        } as NumberCell;
      }),
      ...(summaryColumn
        ? [
            {
              type: "number",
              value: rows.reduce((sum, row) => {
                const cell = row.cells.find((c) => c.groupId === "Summary");
                return (
                  sum +
                  (cell && "value" in cell ? (cell as NumberCell).value : 0)
                );
              }, 0),
              groupId: "Summary",
              readOnly: true,
            } as NumberCell,
          ]
        : []),
    ],
  };

  return { columns, rows: [headerRow, ...rows, summaryRow] };
};

export const getDeltaRows = (
  original: any[],
  current: any[],
  originalCategoryField: string,
  actualCategoryField: string,
  categoryMap: Map<string, string>,
  monthFields: string[],
  monthHeaders: string[],
  groupFn: (catRaw: string) => string,
  normToLabel: (norm: string) => string,
  summaryColumn: boolean,
  unifiedSummary: boolean
) => {
  const getValueMap = (data: any[], categoryField: string) => {
    const map = new Map<string, number>();
    data.forEach((item) => {
      const catNorm = groupFn(item[categoryField] ?? "Iné");
      monthFields.forEach((mf, i) => {
        const val = Number(item[mf]) || 0;
        const key = `${catNorm}-${mf}`;
        map.set(key, (map.get(key) ?? 0) + val);
      });
    });
    return map;
  };

  const originalMap = getValueMap(original, originalCategoryField);
  const currentMap = getValueMap(current, actualCategoryField);

  const columns: Column[] = [
    { columnId: "category", width: 180, resizable: true },
    ...monthHeaders.map((m) => ({ columnId: m, width: 90, resizable: true })),
    ...(summaryColumn
      ? [{ columnId: "Summary", width: 110, resizable: true }]
      : []),
  ];

  const headerRow: Row<DefaultCellTypes> = {
    rowId: "header",
    height: 20,
    cells: [
      {
        type: "text",
        text: "",
        groupId: "category",
        readOnly: true,
      } as TextCell,
      ...monthHeaders.map(
        (m) =>
          ({ type: "text", text: m, groupId: m, readOnly: true } as TextCell)
      ),
      ...(summaryColumn
        ? [
            {
              type: "text",
              text: unifiedSummary ? "Spolu" : "Summary",
              groupId: "Summary",
              readOnly: true,
            } as TextCell,
          ]
        : []),
    ],
  };

  const rows: Row<DefaultCellTypes>[] = Array.from(categoryMap.keys()).map(
    (norm, idx) => {
      let rowTotal = 0;
      const cells: DefaultCellTypes[] = [
        {
          type: "text",
          text: normToLabel(norm),
          groupId: "category",
          readOnly: true,
        } as TextCell,
        ...monthFields.map((mf, i) => {
          const key = `${norm}-${mf}`;
          const delta =
            (currentMap.get(key) ?? 0) - (originalMap.get(key) ?? 0);
          rowTotal += delta;
          return {
            type: "number",
            value: delta,
            groupId: monthHeaders[i],
            readOnly: true,
          } as NumberCell;
        }),
        ...(summaryColumn
          ? [
              {
                type: "number",
                value: rowTotal,
                groupId: "Summary",
                readOnly: true,
              } as NumberCell,
            ]
          : []),
      ];
      return { rowId: idx, height: 20, cells };
    }
  );

  const summaryRow: Row<DefaultCellTypes> = {
    rowId: "summary",
    height: 20,
    cells: [
      {
        type: "text",
        text: unifiedSummary ? "Spolu" : "Summary",
        groupId: "category",
        readOnly: true,
      } as TextCell,
      ...monthHeaders.map((header, idx) => {
        const total = rows.reduce((sum, row) => {
          const cell = row.cells[idx + 1];
          return (
            sum + (cell && "value" in cell ? (cell as NumberCell).value : 0)
          );
        }, 0);
        return {
          type: "number",
          value: total,
          groupId: header,
          readOnly: true,
        } as NumberCell;
      }),
      ...(summaryColumn
        ? [
            {
              type: "number",
              value: rows.reduce((sum, row) => {
                const cell = row.cells.find((c) => c.groupId === "Summary");
                return (
                  sum +
                  (cell && "value" in cell ? (cell as NumberCell).value : 0)
                );
              }, 0),
              groupId: "Summary",
              readOnly: true,
            } as NumberCell,
          ]
        : []),
    ],
  };

  return { columns, rows: [headerRow, ...rows, summaryRow] };
};

interface SummaryTableProps {
  originalTableName: string;
  originalCategoryField: string;
  actualCategoryField: string;
}

const SummaryTable = ({
  originalTableName,
  originalCategoryField,
  actualCategoryField,
}: SummaryTableProps) => {
  const { data } = useData();
  const { selectedEntity } = useSelectedEntity();
  const { selectedTab } = useSelectedTab();
  const { fetchData, fetchNextPage } = useDataverseService();

  const [originalBudget, setOriginalBudget] = useState<any[]>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const loadAllOriginalData = async () => {
      setLoading(true);

      let allRows = [];
      let nextLink = undefined;
      let initial = await fetchData(
        originalTableName,
        undefined,
        selectedEntity?.new_codecompany
      );

      if (!initial || !initial.data?.value) {
        setOriginalBudget([]);
        return;
      }

      allRows = [...initial.data.value];
      nextLink = initial.nextLink;

      while (nextLink) {
        const nextPage = await fetchNextPage(nextLink);
        if (!nextPage?.data?.value) break;

        allRows.push(...nextPage.data.value);
        nextLink = nextPage.nextLink;
      }

      setOriginalBudget(JSON.parse(JSON.stringify(allRows)));
    };

    if (selectedEntity?.new_codecompany) {
      loadAllOriginalData().finally(() => setLoading(false));
    }
  }, [selectedEntity?.new_codecompany]);

  const originalRows = Array.isArray(originalBudget) ? originalBudget : [];
  const currentRows =
    selectedTab == "Summary FTE" ? data.headCount : data.value;

  const categoryMap = useMemo(() => {
    const allRows = [...(currentRows ?? []), ...(originalRows ?? [])];
    const tempMap = new Map<string, string>();

    allRows.forEach((r) => {
      const raw = r[actualCategoryField] ?? r[originalCategoryField] ?? "Iné";
      const norm = normalize(raw);
      if (!tempMap.has(norm)) tempMap.set(norm, raw);
    });

    if (selectedTab == "Sumár - Zjednotená kategória") {
      const desiredOrder = ["lekár", "sestra", "OZP", "THP", "R", "Iné"];
      const normalizedOrder = desiredOrder.map(normalize);

      const sortedEntries = Array.from(tempMap.entries()).sort(([a], [b]) => {
        const ai = normalizedOrder.indexOf(a);
        const bi = normalizedOrder.indexOf(b);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });

      return new Map<string, string>(sortedEntries);
    }

    return tempMap;
  }, [
    currentRows,
    originalRows,
    actualCategoryField,
    originalCategoryField,
    selectedTab,
  ]);

  const normalizedKeys = useMemo(
    () => Array.from(categoryMap.keys()) as string[],
    [categoryMap]
  );

  const originalTableData = useMemo(
    () =>
      buildSummaryRowsByCategory({
        data: originalRows,
        categories: normalizedKeys.map((norm) => categoryMap.get(norm)!),
        monthFields: selectedTab == "Summary FTE" ? numericFields : sumFields,
        monthHeaders: slovakMonthHeaders,
        categoryField: originalCategoryField,
        groupFn: (catRaw) => normalize(catRaw),
        normToLabel: (norm) => categoryMap.get(norm) || norm,
        summaryColumn: selectedTab != "Summary FTE",
        unifiedSummary: selectedTab == "Sumár - Zjednotená kategória",
      }),
    [originalRows, normalizedKeys, categoryMap, originalCategoryField]
  );

  const realTableData = useMemo(
    () =>
      buildSummaryRowsByCategory({
        data: currentRows,
        categories: normalizedKeys.map((norm) => categoryMap.get(norm)!),
        monthFields: selectedTab == "Summary FTE" ? numericFields : sumFields,
        monthHeaders: slovakMonthHeaders,
        categoryField: actualCategoryField,
        groupFn: (catRaw) => normalize(catRaw),
        normToLabel: (norm) => categoryMap.get(norm) || norm,
        summaryColumn: selectedTab != "Summary FTE",
        unifiedSummary: selectedTab == "Sumár - Zjednotená kategória",
      }),
    [currentRows, normalizedKeys, categoryMap, actualCategoryField]
  );

  const deltaTableData = useMemo(
    () =>
      getDeltaRows(
        originalRows,
        currentRows,
        originalCategoryField,
        actualCategoryField,
        categoryMap,
        selectedTab == "Summary FTE" ? numericFields : sumFields,
        slovakMonthHeaders,
        (catRaw) => normalize(catRaw),
        (norm) => categoryMap.get(norm) || norm,
        selectedTab != "Summary FTE",
        selectedTab == "Sumár - Zjednotená kategória"
      ),
    [
      originalRows,
      currentRows,
      categoryMap,
      originalCategoryField,
      actualCategoryField,
    ]
  );

  const [originalColumns, setOriginalColumns] = useState<Column[]>(
    originalTableData.columns
  );
  const [realColumns, setRealColumns] = useState<Column[]>(
    realTableData.columns
  );

  if (loading) return <Loading />;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "40px",
        height: "inherit",
      }}
    >
      <div style={{ minHeight: "33%" }}>
        <label>Original</label>
        <BaseTable
          columns={originalColumns}
          setColumns={setOriginalColumns}
          rows={originalTableData.rows}
          stickyLeftColumns={1}
          customCellTemplates={{
            sortableHeader: new SortableHeaderCellTemplate(),
            summary: new SummaryCellTemplate(),
          }}
          onCellsChanged={() => {}}
          modifyCellValues={() => {}}
        />
      </div>
      <div style={{ minHeight: "33%" }}>
        <label>Actual</label>
        <BaseTable
          columns={realColumns}
          setColumns={setRealColumns}
          rows={realTableData.rows}
          stickyLeftColumns={1}
          customCellTemplates={{
            sortableHeader: new SortableHeaderCellTemplate(),
            summary: new SummaryCellTemplate(),
          }}
          onCellsChanged={() => {}}
          modifyCellValues={() => {}}
        />
      </div>
      <div style={{ minHeight: "33%" }}>
        <label>Delta</label>
        <BaseTable
          columns={originalColumns}
          setColumns={setOriginalColumns}
          rows={deltaTableData.rows}
          stickyLeftColumns={1}
          customCellTemplates={{
            sortableHeader: new SortableHeaderCellTemplate(),
            summary: new SummaryCellTemplate(),
          }}
          onCellsChanged={() => {}}
          modifyCellValues={() => {}}
        />
      </div>
    </div>
  );
};

export default SummaryTable;
